/**
 * News Pulses Loader - Reusable module for loading and displaying news/pulses from backend API
 * 
 * Usage:
 * 1. Include marked.js library: <script src="https://cdn.jsdelivr.net/npm/marked/marked.min.js"></script>
 * 2. Include this file: <script src="scripts/news-pulses-loader.js"></script>
 * 3. Initialize:
 *    const newsLoader = new NewsPulsesLoader({
 *        containerId: 'news-pulses-container',
 *        apiUrl: 'https://web-backend.knowit.today/Yousha/index.php',
 *        userId: '68b11ba594c784d9568b4596',
 *        function: 'getUserPulses'
 *    });
 *    newsLoader.init();
 */

class NewsPulsesLoader {
    constructor(options = {}) {
        // Default configuration
        this.config = {
            containerId: options.containerId || 'news-pulses-container',
            apiUrl: options.apiUrl || 'https://web-backend.knowit.today/Yousha/index.php',
            userId: options.userId || '6951172f94c78494538b46a4',
            function: options.function || 'getUserPulses',
            // Custom templates
            loadingTemplate: options.loadingTemplate || null,
            errorTemplate: options.errorTemplate || null,
            emptyTemplate: options.emptyTemplate || null,
            // Custom render function (optional)
            customRenderPulse: options.customRenderPulse || null,
            // Optional: limit number of items rendered
            maxItems: typeof options.maxItems === 'number' ? options.maxItems : null
        };

        // Check if marked.js is available
        if (typeof marked === 'undefined') {
            console.warn('NewsPulsesLoader: marked.js is not loaded. Markdown rendering will not work.');
        }
    }

    /**
     * Format timestamp to readable date
     */
    formatPulseDate(timestamp) {
        if (!timestamp) return 'Unknown date';
        const date = new Date(timestamp * 1000); // Convert Unix timestamp to milliseconds
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
        if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
        if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
        
        return date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
        });
    }

    /**
     * Format timestamp to objective date (no relative wording)
     */
    formatObjectiveDate(timestamp) {
        if (!timestamp) return 'Unknown date';
        const date = new Date(timestamp * 1000); // Convert Unix timestamp to milliseconds
        return date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
        });
    }

    /**
     * Get display date for pulse - prioritizes display_date > updated_at > created_at
     * Always displays dates objectively (no relative wording)
     */
    getPulseDisplayDate(pulse) {
        // Priority 1: Check for display_date field first
        if (pulse.display_date) {
            // If it's already a string, return it directly
            if (typeof pulse.display_date === 'string') {
                return pulse.display_date;
            }
            // If it's a timestamp, format it objectively
            if (typeof pulse.display_date === 'number') {
                return this.formatObjectiveDate(pulse.display_date);
            }
        }

        // Priority 2: Fall back to updated_at
        if (pulse.updated_at) {
            return this.formatObjectiveDate(pulse.updated_at);
        }

        // Priority 3: Fall back to created_at
        if (pulse.created_at) {
            return this.formatObjectiveDate(pulse.created_at);
        }

        return 'Unknown date';
    }

    /**
     * Fetch pulses from backend API
     */
    async fetchPulses() {
        try {
            const response = await fetch(
                "/api",
                 {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    'function': this.config.function,
                    'user_id': this.config.userId
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error fetching pulses:', error);
            throw error;
        }
    }

    /**
     * Clean up rendered HTML to remove empty paragraphs and normalize spacing
     */
    cleanupMarkdownHtml(html) {
        // Create a temporary div to parse and clean the HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        
        // Remove paragraphs that only contain whitespace or are empty
        const paragraphs = tempDiv.querySelectorAll('p');
        paragraphs.forEach(p => {
            const text = p.textContent.trim();
            // Check if paragraph is empty or only contains line breaks
            if (!text || text === '' || (p.children.length === 1 && p.querySelector('br'))) {
                p.remove();
            }
        });
        
        return tempDiv.innerHTML;
    }

    /**
     * Render a single pulse/article
     */
    renderPulse(pulse) {
        // If custom render function is provided, use it
        if (this.config.customRenderPulse && typeof this.config.customRenderPulse === 'function') {
            return this.config.customRenderPulse(pulse, this);
        }

        // Default rendering
        let htmlContent = '';
        
        // Render markdown content if marked.js is available
        if (typeof marked !== 'undefined') {
            // Configure marked.js to reduce spacing
            marked.setOptions({
                breaks: false,
                gfm: true,
                headerIds: false,
                mangle: false
            });
            
            const markdownContent = pulse.content || '';
            htmlContent = marked.parse(markdownContent);
            
            // Clean up empty paragraphs and excessive whitespace
            htmlContent = this.cleanupMarkdownHtml(htmlContent);
            
            // Remove extra line breaks and normalize whitespace
            htmlContent = htmlContent.replace(/\n\s*\n\s*\n/g, '\n\n');
        } else {
            // Fallback: escape HTML and preserve line breaks
            htmlContent = (pulse.content || '').replace(/\n/g, '<br>');
        }

        // Get display date (prioritizes display_date if available)
        const displayDate = this.getPulseDisplayDate(pulse);
        const hasDisplayDate = pulse.display_date;
        
        // Get first image for preview (if available)
        const firstImage = pulse.images && pulse.images.length > 0 ? pulse.images[0] : null;
        const hasMultipleImages = pulse.images && pulse.images.length > 1;
        
        // Render tags if available
        let tagsHtml = '';
        if (pulse.tags && pulse.tags.length > 0) {
            tagsHtml = '<div class="pulse-tags">' +
                pulse.tags.map(tag => 
                    `<span class="pulse-tag">${tag}</span>`
                ).join('') +
                '</div>';
        }
        
        // Store pulse data as JSON for modal - use HTML entity encoding
        // First stringify, then escape for HTML attribute (use double quotes for attribute)
        const pulseData = JSON.stringify(pulse)
            .replace(/&/g, '&amp;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');
        
        return `
            <article class="news-article dynamic-post" data-pulse="${pulseData}">
                <div class="news-content-wrapper">
                    <div class="news-meta">
                        <span class="news-date">${displayDate}</span>
                    </div>
                    <div class="markdown-content">
                        ${htmlContent}
                    </div>
                    ${tagsHtml}
                </div>
                ${firstImage ? `
                    <div class="news-image-wrapper">
                        <img src="${firstImage}" alt="News image" loading="lazy" />
                        ${hasMultipleImages ? `<span class="image-count-badge">+${pulse.images.length - 1} more</span>` : ''}
                    </div>
                ` : ''}
            </article>
        `;
    }

    /**
     * Get sort timestamp for a pulse
     * Priority: display_date > updated_at > created_at
     */
    getSortTimestamp(pulse) {
        // Use display_date if it exists and is a number
        if (pulse.display_date && typeof pulse.display_date === 'number') {
            return pulse.display_date;
        }
        // Use updated_at if it exists
        if (pulse.updated_at) {
            return pulse.updated_at;
        }
        // Fall back to created_at
        return pulse.created_at || 0;
    }

    /**
     * Render all pulses
     */
    renderPulses(pulses) {
        const container = document.getElementById(this.config.containerId);
        if (!container) {
            console.error(`NewsPulsesLoader: Container with ID "${this.config.containerId}" not found`);
            return;
        }

        if (!pulses || pulses.length === 0) {
            if (this.config.emptyTemplate) {
                container.innerHTML = this.config.emptyTemplate;
            } else {
                container.innerHTML = `
                    <div class="error-news">
                        <p>No news updates available at this time.</p>
                    </div>
                `;
            }
            return;
        }

        // Sort by display_date > updated_at > created_at (newest first)
        const sortedPulses = [...pulses].sort((a, b) => {
            const timestampA = this.getSortTimestamp(a);
            const timestampB = this.getSortTimestamp(b);
            return timestampB - timestampA;
        });

        // Apply maxItems limit if configured
        const pulsesToRender = this.config.maxItems
            ? sortedPulses.slice(0, this.config.maxItems)
            : sortedPulses;

        container.innerHTML = pulsesToRender.map(pulse => this.renderPulse(pulse)).join('');
        
        // Attach click handlers to open modal
        this.attachModalHandlers();
    }
    
    /**
     * Attach click handlers for modal
     */
    attachModalHandlers() {
        const articles = document.querySelectorAll(`#${this.config.containerId} .news-article.dynamic-post`);
        articles.forEach(article => {
            article.addEventListener('click', (e) => {
                // Don't open modal if clicking on links or buttons
                if (e.target.tagName === 'A' || e.target.tagName === 'BUTTON') {
                    return;
                }
                
                const pulseData = article.getAttribute('data-pulse');
                if (pulseData) {
                    try {
                        // Decode HTML entities back to JSON string
                        const decodedData = pulseData
                            .replace(/&quot;/g, '"')
                            .replace(/&#39;/g, "'")
                            .replace(/&amp;/g, '&')
                            .replace(/&lt;/g, '<')
                            .replace(/&gt;/g, '>');
                        const pulse = JSON.parse(decodedData);
                        this.openModal(pulse);
                    } catch (error) {
                        console.error('Error parsing pulse data:', error);
                        console.error('Pulse data:', pulseData);
                    }
                }
            });
        });
    }
    
    /**
     * Open modal with full pulse content
     */
    openModal(pulse) {
        // Create modal if it doesn't exist
        let modal = document.getElementById('news-pulse-modal');
        if (!modal) {
            modal = document.createElement('div');
            modal.id = 'news-pulse-modal';
            modal.className = 'news-modal';
            document.body.appendChild(modal);
        }
        
        // Render markdown content for modal
        let htmlContent = '';
        if (typeof marked !== 'undefined') {
            marked.setOptions({
                breaks: false,
                gfm: true,
                headerIds: false,
                mangle: false
            });
            
            const markdownContent = pulse.content || '';
            htmlContent = marked.parse(markdownContent);
            htmlContent = this.cleanupMarkdownHtml(htmlContent);
        } else {
            htmlContent = (pulse.content || '').replace(/\n/g, '<br>');
        }
        
        // Render all images
        let imagesHtml = '';
        if (pulse.images && pulse.images.length > 0) {
            imagesHtml = '<div class="news-modal-images">' +
                pulse.images.map(img => 
                    `<img src="${img}" alt="News image" loading="lazy" />`
                ).join('') +
                '</div>';
        }
        
        // Render tags
        let tagsHtml = '';
        if (pulse.tags && pulse.tags.length > 0) {
            tagsHtml = '<div class="pulse-tags">' +
                pulse.tags.map(tag => 
                    `<span class="pulse-tag">${tag}</span>`
                ).join('') +
                '</div>';
        }
        
        const displayDate = this.getPulseDisplayDate(pulse);
        const hasDisplayDate = pulse.display_date;
        
        modal.innerHTML = `
            <div class="news-modal-content">
                <div class="news-modal-header">
                    <div>
                        <span class="news-date">${displayDate}</span>
                        ${!hasDisplayDate && pulse.updated_at && pulse.updated_at !== pulse.created_at ? 
                            `<span class="news-updated">Updated: ${this.formatObjectiveDate(pulse.updated_at)}</span>` : ''}
                    </div>
                    <button class="news-modal-close" aria-label="Close">&times;</button>
                </div>
                <div class="news-modal-body">
                    <div class="markdown-content">
                        ${htmlContent}
                    </div>
                    ${tagsHtml}
                    ${imagesHtml}
                </div>
            </div>
        `;
        
        // Show modal
        modal.classList.add('active');
        
        // Close handlers
        const closeBtn = modal.querySelector('.news-modal-close');
        closeBtn.addEventListener('click', () => this.closeModal());
        
        modal.addEventListener('click', (e) => {
            if (e.target === modal) {
                this.closeModal();
            }
        });
        
        // Close on Escape key
        const escapeHandler = (e) => {
            if (e.key === 'Escape') {
                this.closeModal();
                document.removeEventListener('keydown', escapeHandler);
            }
        };
        document.addEventListener('keydown', escapeHandler);
    }
    
    /**
     * Close modal
     */
    closeModal() {
        const modal = document.getElementById('news-pulse-modal');
        if (modal) {
            modal.classList.remove('active');
            // Remove modal after animation
            setTimeout(() => {
                if (modal.parentNode) {
                    modal.parentNode.removeChild(modal);
                }
            }, 300);
        }
    }

    /**
     * Show loading state
     */
    showLoading() {
        const container = document.getElementById(this.config.containerId);
        if (container) {
            if (this.config.loadingTemplate) {
                container.innerHTML = this.config.loadingTemplate;
            } else {
                container.innerHTML = `
                    <div class="loading-news">
                        <div class="loading-spinner"></div>
                        <p>Loading latest updates...</p>
                    </div>
                `;
            }
        }
    }

    /**
     * Show error state
     */
    showError(error) {
        const container = document.getElementById(this.config.containerId);
        if (container) {
            if (this.config.errorTemplate) {
                container.innerHTML = this.config.errorTemplate.replace('{message}', error.message || 'An error occurred');
            } else {
                container.innerHTML = `
                    <div class="error-news">
                        <p>⚠️ Unable to load latest updates</p>
                        <p class="error-details">${error.message || 'An error occurred'}</p>
                        <button onclick="location.reload()" class="btn btn-primary btn-small">Refresh Page</button>
                    </div>
                `;
            }
        }
    }

    /**
     * Extract pulses from API response (handles different response formats)
     */
    extractPulses(data) {
        if (Array.isArray(data)) {
            return data;
        } else if (data.data && Array.isArray(data.data)) {
            return data.data;
        } else if (data.pulses && Array.isArray(data.pulses)) {
            return data.pulses;
        } else if (data.result && Array.isArray(data.result)) {
            return data.result;
        }
        return [];
    }

    /**
     * Initialize news loader
     */
    async init() {
        this.showLoading();
        
        try {
            const data = await this.fetchPulses();
            const pulses = this.extractPulses(data);
            this.renderPulses(pulses);
        } catch (error) {
            console.error('Failed to load news:', error);
            this.showError(error);
        }
    }

    /**
     * Refresh/Reload news
     */
    async refresh() {
        await this.init();
    }
}

// Export for use in other scripts
if (typeof module !== 'undefined' && module.exports) {
    module.exports = NewsPulsesLoader;
} else {
    window.NewsPulsesLoader = NewsPulsesLoader;
}

// ES module export
export { NewsPulsesLoader };


